import React, { useCallback, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Tabs } from '@bringg/react-components';
import { Task } from '@bringg/types';
import { withErrorBoundary } from '@bringg-frontend/bringg-web-infra';

import notification from 'bringg-web/services/notification';
import { createObserver, ObserverId } from 'bringg-web/features/exceptions-management/hooks/use-in-view';
import { useStores } from 'bringg-web/recipes';
import { TRANSLATION_DOMAINS } from 'bringg-web/consts';
import type { AlertGroup } from 'bringg-web/types/common.consts';
import { TabLabel } from '../tab-label/tab-label';
import { GetWeightAndTotalWeightFunction, weightService } from '../services/weight-service';
import TaskAlertsTab from './task-alerts-tab';
import { DriverAlertsTab } from './driver-alerts-tab';
import { OptimizationAlertsTab } from './optimization-alerts-tab';
import { DismissConfirm, useDismissConfirm, DismissConfirmContext } from '../dismiss-confirm';
import { ALERT_TABS_ID, NEW_ALERT_CLASS } from '../consts';
import { workflowsRootStore } from 'bringg-web/features/automation-workflows/stores/internal';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';
import { PRESETS_VIEWS_FEATURE_FLAG } from 'bringg-web/features/preset-views/utils';

import './alert-tabs.scss';

interface TabConfigItem {
	tabKey: AlertGroup;
	labelKey: string;
}

const { EXCEPTIONS_LIST } = TRANSLATION_DOMAINS;
const TABS_CONFIG: TabConfigItem[] = [
	{ tabKey: 'taskAlerts', labelKey: `${EXCEPTIONS_LIST}.ORDER_ALERTS` },
	{ tabKey: 'driverAlerts', labelKey: `${EXCEPTIONS_LIST}.DRIVER_ALERTS` },
	{ tabKey: 'optimizationAlerts', labelKey: `${EXCEPTIONS_LIST}.OPTIMISATION_ALERTS` }
];

interface Props {
	deps: {
		getWeightAndTotalWeight: GetWeightAndTotalWeightFunction;
		isAllowedToAssignSpecificTask: (task: Task) => boolean;
	};
}

const AlertTabs = ({ deps }: Props) => {
	const {
		alertsViewStore: { unseenAlertsManager },
		alertsStore: { fetchAlerts, dismissAlertsByIds, fetchTaskAlertsData, setShouldLoadTaskOnAlertSocketEvent },
		teamsStore: { fetchAll: fetchAllTeams },
		driversStore: { fetchAll: fetchAllDrivers },
		companiesStore: { fetchAll: fetchAllCompanies }
	} = useStores();
	const {
		workflowAlertsStore: { isLoading: isLoadingConfigurations }
	} = workflowsRootStore.getStore();
	const { t } = useTranslation();
	const { dismissModalVisible, setDismissModalVisible, setIdsToDismiss, idsToDismiss } = useDismissConfirm();
	const presetViewsEnabled = useHasFeatureFlag(PRESETS_VIEWS_FEATURE_FLAG);

	useEffect(() => {
		if (deps?.getWeightAndTotalWeight) {
			weightService.attachWeightAndTotalWeightFunc(deps.getWeightAndTotalWeight);
		}
	}, [deps]);

	// TODO: teams store is empty if fetch is not triggered here, figure out if it should be like that
	useEffect(() => {
		fetchAllTeams();
		// 	fetchAllFleets();
		// TODO: refactor to load initial batch of data `n` items
		// and add to driver-cell logic to fetch driver data in case of driver not exist in store
		fetchAllDrivers({ limit: 5000 });
		fetchAllCompanies();
	}, []);

	useEffect(() => {
		// fetch alerts only after configuration is loaded in case the component code will run before and will miss alert types in the req.
		if (!isLoadingConfigurations) {
			const fetchData = async () => {
				await fetchAlerts({ limit: 5000 });
				await fetchTaskAlertsData();
				setShouldLoadTaskOnAlertSocketEvent(true);
			};
			fetchData();
		}
	}, [isLoadingConfigurations]);

	// Initialize IntersectionObserver used for alerts screen
	useEffect(() => {
		createObserver(ObserverId.AlertsScreen, (entries, intersectionObserver) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					const itemId = entry.target.getAttribute('data-alert-id');
					// Slight perf improvement
					intersectionObserver.unobserve(entry.target);
					if (unseenAlertsManager.allNewAlerts.has(itemId)) {
						unseenAlertsManager.remove(itemId);
					}
					// Kinda hacking in DOM, but should be ok and save us time on row re-render
					// This is needed only for rows which are in view before container is scrolled
					entry.target.classList.remove(NEW_ALERT_CLASS);
				}
			});
		});
	}, []);

	const dismissConfirmContext = useMemo(
		() => ({
			setDismissModalVisible,
			setIdsToDismiss
		}),
		[setDismissModalVisible, setIdsToDismiss]
	);

	const handleCloseDismissModal = useCallback(() => setDismissModalVisible(false), [setDismissModalVisible]);
	const dismissAlerts = useCallback(async () => {
		const ids: string[] = Array.from(idsToDismiss);
		// Reset state directly might be still the best option despite useFilteredRowSelect handles
		// the case of removed rows selected state, observe for any perf degradation
		// toggleAllRowsSelected(false);
		try {
			await dismissAlertsByIds(ids);
			notification.success(t('EXCEPTIONS_LIST.ALERTS_DISMISSED'));
		} catch (error) {
			notification.error(t('ERRORS.ERROR_OCCURRED'));
		}
		setDismissModalVisible(false);
	}, [idsToDismiss]);

	return (
		<>
			<DismissConfirmContext.Provider value={dismissConfirmContext}>
				<Tabs
					id={ALERT_TABS_ID}
					destroyInactiveTabPane
					defaultActiveKey={TABS_CONFIG[0].tabKey}
					className="alert-tabs"
					items={[
						{
							key: TABS_CONFIG[0].tabKey,
							label: (
								<TabLabel
									translateKey={TABS_CONFIG[0].labelKey}
									badgeText={unseenAlertsManager.taskAlerts.size}
								/>
							),
							children: <TaskAlertsTab deps={deps} presetViewsEnabled={presetViewsEnabled} />
						},
						{
							key: TABS_CONFIG[1].tabKey,
							label: (
								<TabLabel
									translateKey={TABS_CONFIG[1].labelKey}
									badgeText={unseenAlertsManager.driverAlerts.size}
								/>
							),
							children: <DriverAlertsTab presetViewsEnabled={presetViewsEnabled} />
						},
						{
							key: TABS_CONFIG[2].tabKey,
							label: (
								<TabLabel
									translateKey={TABS_CONFIG[2].labelKey}
									badgeText={unseenAlertsManager.optimizationAlerts.size}
								/>
							),
							children: <OptimizationAlertsTab presetViewsEnabled={presetViewsEnabled} />
						}
					]}
				/>
			</DismissConfirmContext.Provider>

			<DismissConfirm
				visible={dismissModalVisible}
				countToDismiss={idsToDismiss.length}
				onOk={dismissAlerts}
				onCancel={handleCloseDismissModal}
			/>
		</>
	);
};

export default withErrorBoundary(observer(AlertTabs));
